import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { List, X, CaretDown, PhoneCall } from '@phosphor-icons/react';
import { NAV, SERVICES, CONTACT } from '@/data/site';

const Logo = ({ variant = 'dark' }) => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
    <img src="/logo-mark.png" alt="Inclusive Mobility Group" className="h-11 w-auto group-hover:scale-105 transition-transform duration-300" />
    <div className="leading-none flex flex-col">
      <span className={`font-display font-extrabold text-[17px] tracking-tight ${variant === 'light' ? 'text-white' : 'text-ink'}`}>
        Inclusive <span className="text-euca">Mobility</span>
      </span>
      <span className={`text-[10px] tracking-[0.25em] font-semibold uppercase ${variant === 'light' ? 'text-white/70' : 'text-ink-muted'}`}>
        Group · Australia
      </span>
    </div>
  </Link>
);

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-porcelain/90 backdrop-blur-xl border-b border-ink/10 shadow-[0_4px_20px_rgba(26,26,26,0.04)]'
            : 'bg-porcelain/70 backdrop-blur-md'
        }`}
      >
        <div className="container-x flex items-center justify-between py-4 md:py-5">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.main.map((n) => {
              if (n.label === 'Services') {
                return (
                  <div
                    key={n.label}
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <button
                      data-testid="nav-services-trigger"
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-ink-soft hover:text-ink transition"
                    >
                      Services <CaretDown size={14} weight="bold" />
                    </button>
                    {servicesOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3">
                        <div className="w-[720px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(17,24,39,0.15)] border border-neutral-200 p-5 grid grid-cols-2 gap-1">
                          <Link
                            to="/services"
                            data-testid="nav-services-all"
                            className="col-span-2 flex items-center justify-between p-3 rounded-xl hover:bg-neutral-50 transition group"
                          >
                            <div>
                              <div className="font-semibold text-ink">All services</div>
                              <div className="text-xs text-ink-muted mt-0.5">Every transport service Inclusive Mobility Group offers, in one place.</div>
                            </div>
                            <span className="text-brand font-semibold text-sm">Explore →</span>
                          </Link>
                          {SERVICES.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/services/${s.slug}`}
                              data-testid={`nav-service-${s.slug}`}
                              className="p-3 rounded-xl hover:bg-neutral-50 transition group"
                            >
                              <div className="font-semibold text-ink text-sm group-hover:text-brand transition">{s.name}</div>
                              <div className="text-xs text-ink-muted mt-0.5 line-clamp-1">{s.tagline}</div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={n.label}
                  to={n.to}
                  end={n.to === '/'}
                  data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-semibold transition whitespace-nowrap ${
                      isActive ? 'text-brand' : 'text-ink-soft hover:text-ink'
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${CONTACT.phone_dial}`} data-testid="nav-phone" className="flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-brand transition-colors whitespace-nowrap">
              <PhoneCall size={15} weight="bold" className="text-brand" /> {CONTACT.phone_display}
            </a>
            <Link to="/book" data-testid="nav-book-btn" className="btn-primary text-sm whitespace-nowrap">
              Book Transport
            </Link>
          </div>

          <button
            onClick={() => setOpen(v => !v)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center"
            aria-label="Menu"
          >
            {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-white pt-20 overflow-y-auto" data-testid="nav-mobile-menu">
          <div className="container-x py-6">
            <div className="space-y-1">
              {NAV.main.map((n) => (
                <NavLink
                  key={n.label}
                  to={n.to}
                  end={n.to === '/'}
                  data-testid={`nav-mobile-link-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `block px-4 py-4 rounded-xl text-lg font-semibold border-b border-neutral-100 ${
                      isActive ? 'text-brand bg-brand/5' : 'text-ink hover:bg-neutral-50'
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <details className="border-b border-neutral-100">
                <summary className="px-4 py-4 text-lg font-semibold text-ink cursor-pointer flex items-center justify-between">
                  All Services <CaretDown size={16} />
                </summary>
                <div className="pl-4 pb-3 space-y-1">
                  {SERVICES.map(s => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`nav-mobile-service-${s.slug}`}
                      className="block px-4 py-2 rounded-lg text-sm text-ink-soft hover:bg-neutral-50"
                    >
                      {s.name}
                    </Link>
                  ))}
                </div>
              </details>
              <Link to="/careers" className="block px-4 py-4 text-lg font-semibold text-ink border-b border-neutral-100">Careers</Link>
              <Link to="/faq" className="block px-4 py-4 text-lg font-semibold text-ink border-b border-neutral-100">FAQ</Link>
              <Link to="/refer" className="block px-4 py-4 text-lg font-semibold text-ink border-b border-neutral-100">Referrals</Link>
            </div>
            <div className="mt-6 space-y-3">
              <Link to="/quote" data-testid="nav-mobile-quote" className="btn-secondary w-full">Instant Quote</Link>
              <Link to="/book" data-testid="nav-mobile-book" className="btn-primary w-full">Book Transport</Link>
              <a href={`tel:${CONTACT.phone_dial}`} className="block text-center py-3 text-sm font-semibold text-brand">
                {CONTACT.phone_display} · 24/7
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Spacer */}
      <div className="h-[76px] md:h-[80px]" />
    </>
  );
}
